/**
 * Embla Carousel Manager
 * A global utility to manage Embla Carousel instances across the site
 */
if (!window.EmblaCentralManager) {
    class EmblaCentralManager {
        constructor() {
            this.instances = {};
            this.carouselData = {}; // Store viewportNode and options for each carousel
            this.initialized = false;
            this.loadingPromise = null;
        }

        /**
         * Load Embla Carousel script if not already loaded
         * @returns {Promise} - Resolves when Embla is loaded
         */
        loadEmblaScript() {
            if (window.EmblaCarousel) {
                return Promise.resolve();
            }

            if (this.loadingPromise) {
                return this.loadingPromise;
            }

            this.loadingPromise = new Promise((resolve, reject) => {
                const script = document.createElement("script");
                script.src =
                    "https://unpkg.com/embla-carousel/embla-carousel.umd.js";
                script.async = true;

                script.onload = () => {
                    // console.log("Embla Carousel script loaded");
                    this.initialized = true;
                    resolve();
                };

                script.onerror = (error) => {
                    console.error(
                        "Error loading Embla Carousel script:",
                        error
                    );
                    reject(error);
                };

                document.head.appendChild(script);
            });

            return this.loadingPromise;
        }

        /**
         * Initialize or reinitialize a carousel
         * @param {string} id - Unique identifier for this carousel instance
         * @param {HTMLElement} viewportNode - The carousel viewport element
         * @param {Object} options - Embla Carousel options
         * @param {Function} afterInit - Callback to run after initialization (optional)
         * @returns {Object} - The Embla instance
         */
        initCarousel(id, viewportNode, options, afterInit = null) {
            try {
                // Check if EmblaCarousel is available
                if (!window.EmblaCarousel) {
                    console.error(
                        "EmblaCarousel is not loaded. Cannot initialize carousel:",
                        id
                    );
                    return null;
                }

                // Check for valid viewport node
                if (!viewportNode || !viewportNode.ownerDocument) {
                    console.error(
                        "Invalid viewport node provided for carousel:",
                        id
                    );
                    return null;
                }

                // Clean up existing instance if it exists
                this.destroyCarousel(id);

                // Store the parameters for later reinitializations
                this.carouselData[id] = {
                    viewportNode,
                    options,
                    afterInit,
                };

                // Create new instance
                this.instances[id] = window.EmblaCarousel(
                    viewportNode,
                    options
                );

                // Run callback if provided
                if (afterInit && typeof afterInit === "function") {
                    try {
                        afterInit(this.instances[id]);
                    } catch (callbackError) {
                        console.error(
                            "Error in carousel callback:",
                            callbackError
                        );
                    }
                }

                return this.instances[id];
            } catch (error) {
                console.error("Error initializing carousel:", id, error);
                return null;
            }
        }

        /**
         * Destroy a carousel instance if it exists
         * @param {string} id - Unique identifier for this carousel instance
         */
        destroyCarousel(id) {
            try {
                if (
                    this.instances[id] &&
                    typeof this.instances[id].destroy === "function"
                ) {
                    this.instances[id].destroy();
                    delete this.instances[id];
                }
            } catch (error) {
                console.error("Error destroying carousel:", id, error);
            }
        }

        /**
         * Destroy all carousel instances
         */
        destroyAllCarousels() {
            Object.keys(this.instances).forEach((id) => {
                this.destroyCarousel(id);
            });
        }

        /**
         * Reinitialize a carousel
         * @param {string} id - Unique identifier for this carousel instance
         */
        reInitCarousel(id) {
            try {
                if (
                    this.instances[id] &&
                    typeof this.instances[id].reInit === "function"
                ) {
                    this.instances[id].reInit();
                    return true;
                } else if (this.carouselData[id]) {
                    // If the instance doesn't exist or doesn't have reInit, try to recreate it
                    const { viewportNode, options, afterInit } =
                        this.carouselData[id];

                    // Check if viewportNode is still in the document
                    if (viewportNode && viewportNode.isConnected) {
                        return this.initCarousel(
                            id,
                            viewportNode,
                            options,
                            afterInit
                        );
                    } else {
                        console.warn(
                            `Cannot reinitialize carousel ${id}: viewport node is no longer in the document`
                        );
                        // Clean up stored data for this invalid carousel
                        delete this.carouselData[id];
                        return null;
                    }
                }
                return null;
            } catch (error) {
                console.error("Error reinitializing carousel:", id, error);
                return null;
            }
        }

        /**
         * Reinitialize all carousels
         */
        reInitAllCarousels() {
            Object.keys(this.instances).forEach((id) => {
                this.reInitCarousel(id);
            });
        }

        /**
         * Handle responsive resize for all carousels
         * Debounced to prevent too many reinitialization calls
         */
        setupResponsiveHandling() {
            let resizeTimer;
            window.addEventListener("resize", () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(() => {
                    this.reInitAllCarousels();
                }, 250);
            });
        }
    }

    // Make the class available globally
    window.EmblaCentralManager = EmblaCentralManager;
}

// Create global instance
window.emblaCarouselManager =
    window.emblaCarouselManager || new window.EmblaCentralManager();

// Setup responsive handling when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
    window.emblaCarouselManager.setupResponsiveHandling();
});
